'use client';

import { useState, useEffect } from 'react';
import { CartItemPopulated } from '@/lib/Apis/models/cart-item-populated';
import { ProductsApi } from '@/lib/Apis/apis/products-api';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';

interface OrderProductData {
    images: Record<string, string>;
    names: Record<string, string>;
}

/**
 * Fetches product images and names for order items whose variant has no media / no productName.
 * Returns maps of productId -> image URL and productId -> product name.
 */
export function useOrderProductImages(items: CartItemPopulated[] | undefined): Record<string, string> {
    const [productImages, setProductImages] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!items?.length) return;
        const itemsWithoutImage = items.filter(item => !item.productVariantData?.media?.length);
        if (!itemsWithoutImage.length) return;

        const uniqueProductIds = [...new Set(
            itemsWithoutImage.map(item => (item.productVariantData as any).productId).filter(Boolean)
        )] as string[];
        if (!uniqueProductIds.length) return;

        const api = new ProductsApi(AXIOS_CONFIG);
        Promise.all(
            uniqueProductIds.map(async productId => {
                try {
                    const res = await api.getSingleProduct(productId);
                    const image = res.data?.media?.[0]?.file;
                    return image ? [productId, image] as const : null;
                } catch {
                    return null;
                }
            })
        ).then(results => {
            const map: Record<string, string> = {};
            results.forEach(r => { if (r) map[r[0]] = r[1]; });
            setProductImages(map);
        });
    }, [items]);

    return productImages;
}

/**
 * Fetches product images and names for order items.
 * Returns maps of productId -> image URL and productId -> product name.
 */
export function useOrderProductData(items: CartItemPopulated[] | undefined): OrderProductData {
    const [data, setData] = useState<OrderProductData>({ images: {}, names: {} });

    useEffect(() => {
        if (!items?.length) return;

        const uniqueProductIds = [...new Set(
            items.map(item => (item.productVariantData as any).productId).filter(Boolean)
        )] as string[];
        if (!uniqueProductIds.length) return;

        const api = new ProductsApi(AXIOS_CONFIG);
        Promise.all(
            uniqueProductIds.map(async productId => {
                try {
                    const res = await api.getSingleProduct(productId);
                    return { productId, image: res.data?.media?.[0]?.file, name: (res.data as any)?.name } as const;
                } catch {
                    return null;
                }
            })
        ).then(results => {
            const images: Record<string, string> = {};
            const names: Record<string, string> = {};
            results.forEach(r => {
                if (!r) return;
                if (r.image) images[r.productId] = r.image;
                if (r.name) names[r.productId] = r.name;
            });
            setData({ images, names });
        });
    }, [items]);

    return data;
}
